import { apiSlice, ErrorType, TemplateOrCalculation } from './api';
import {
  BrandDetailResponse,
  CalculationDepartmentResponse,
  Condition,
  DepartmentResponse,
  Operation,
  RegionResponse,
  SkuClassResponse,
  SortDirection,
  Sorting,
  StoreResponse,
  StoreTypeResponse,
} from '@ydistri/api-sdk';
import {
  BrandsCollection,
  CalculationsCollection,
  SkuClassesCollection,
  Stores2Collection,
  TemplatesCollection,
} from '../swagger/collections';
import { getTags } from './apiLib';
import { toBrandDetailResponse } from '../lib/brands/brandsLib';
import { NO_VALUE } from '../lib/utils/utilsTypes';

const { TAGS, TAGS_ARRAY } = getTags('lists');

export interface ListType<T> {
  array: T[];
  object: Record<number, T | undefined>;
}

const reduceDataToListTypeObject = <T extends { id: number }>(
  data: T[],
  sorter?: (a: T, b: T) => number,
): ListType<T> => {
  const obj: ListType<T>['object'] = {};
  data.reduce((acc, slice) => {
    acc[slice.id] = slice;
    return acc;
  }, obj);

  return { array: sorter ? data.toSorted(sorter) : data, object: obj };
};

export interface SkuClassesRequest {
  skuClassId?: number;
  sortByName?: boolean;
}

export type GetSkuClassesRequest = TemplateOrCalculation | SkuClassesRequest | undefined;

const isTemplateOrCalculation = (request: GetSkuClassesRequest): request is TemplateOrCalculation =>
  request !== undefined && 'type' in request;

export const apiLists = apiSlice.enhanceEndpoints({ addTagTypes: TAGS_ARRAY }).injectEndpoints({
  endpoints: builder => ({
    getBrands: builder.query<BrandDetailResponse[], TemplateOrCalculation | undefined>({
      queryFn: async arg => {
        try {
          if (!arg) {
            const response = await BrandsCollection.brandsList();
            return { data: response.data.data };
          }

          const response = await (arg.type === 'Template'
            ? TemplatesCollection.getTemplateBrands(arg.id)
            : CalculationsCollection.getCalculationBrands(arg.id));

          return { data: response.data.data.map(toBrandDetailResponse) };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      providesTags: [TAGS.brandsList],
    }),

    getSkuClasses: builder.query<SkuClassResponse[], GetSkuClassesRequest>({
      queryFn: async arg => {
        try {
          if (isTemplateOrCalculation(arg)) {
            const response = await (arg.type === 'Template'
              ? TemplatesCollection.getTemplateSkuClassConfigurations(arg.id)
              : CalculationsCollection.getCalculationSkuClassConfigurations(arg.id));
            const skuClasses = response.data.data.flatMap(configuration =>
              configuration.skuClass ? [configuration.skuClass] : [],
            );

            return { data: skuClasses };
          }

          const { skuClassId, sortByName } = arg ?? {};
          if (skuClassId === NO_VALUE) return { data: [] };

          const conditions: Condition[] = [];
          if (skuClassId) {
            conditions.push({ fieldName: 'Id', operation: Operation.Eq, value: skuClassId });
          }

          const sortings: Sorting[] | undefined = sortByName
            ? [{ fieldName: 'Name', direction: SortDirection.Asc }]
            : undefined;

          const response = await SkuClassesCollection.skuClassesList({ conditions, sortings });

          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      providesTags: [TAGS.skuClassesList],
    }),

    getStores: builder.query<ListType<StoreResponse>, TemplateOrCalculation>({
      queryFn: async arg => {
        const response = await (arg.type === 'Template'
          ? TemplatesCollection.getTemplateStores(arg.id)
          : CalculationsCollection.getCalculationStores(arg.id));

        return {
          data: reduceDataToListTypeObject(response.data.data),
        };
      },
      providesTags: [TAGS.storesList],
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- the collection takes no arguments
    getProjectStores: builder.query<ListType<StoreResponse>, void>({
      queryFn: async () => {
        const response = await Stores2Collection.getStores();

        return {
          data: reduceDataToListTypeObject(response.data.data),
        };
      },
      providesTags: [TAGS.storesList],
    }),

    getRegions: builder.query<ListType<RegionResponse>, TemplateOrCalculation>({
      queryFn: async arg => {
        const response = await (arg.type === 'Template'
          ? TemplatesCollection.getTemplateRegions(arg.id)
          : CalculationsCollection.getCalculationRegions(arg.id));

        return {
          data: reduceDataToListTypeObject(response.data.data, (left, right) =>
            left.name.localeCompare(right.name),
          ),
        };
      },
      providesTags: [TAGS.regionsList],
    }),

    getStoreTypes: builder.query<ListType<StoreTypeResponse>, TemplateOrCalculation>({
      queryFn: async arg => {
        const response = await (arg.type === 'Template'
          ? TemplatesCollection.getTemplateStoreTypes(arg.id)
          : CalculationsCollection.getCalculationStoreTypes(arg.id));

        return { data: reduceDataToListTypeObject(response.data.data) };
      },
      providesTags: [TAGS.storeTypesList],
    }),

    getDepartments: builder.query<
      ListType<DepartmentResponse | CalculationDepartmentResponse>,
      TemplateOrCalculation
    >({
      queryFn: async arg => {
        const response = await (arg.type === 'Template'
          ? TemplatesCollection.getTemplateDepartments(arg.id)
          : CalculationsCollection.getCalculationDepartments(arg.id));

        return { data: reduceDataToListTypeObject(response.data.data) };
      },
      providesTags: [TAGS.departmentsList],
    }),
  }),
});

export const {
  useGetBrandsQuery,
  useGetSkuClassesQuery,
  useGetRegionsQuery,
  useGetStoresQuery,
  useGetProjectStoresQuery,
  useGetStoreTypesQuery,
  useGetDepartmentsQuery,
} = apiLists;
